import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function AccountingPage() {
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category:"Rent", amount:0, description:"" });

  const load = async () => {
    const [{data:p},{data:e}] = await Promise.all([api.get("/payments"), api.get("/expenses")]);
    setPayments(p); setExpenses(e);
  };
  useEffect(()=>{load()},[]);

  const income = payments.reduce((s,p)=>s+Number(p.amount||0), 0);
  const expenseTotal = expenses.reduce((s,p)=>s+Number(p.amount||0), 0);
  const profit = income - expenseTotal;

  const addExpense = async (e) => {
    e.preventDefault();
    await api.post("/expenses", { ...form, amount: Number(form.amount), date: new Date().toISOString().slice(0,10) });
    setOpen(false); setForm({ category:"Rent", amount:0, description:"" });
    toast.success("Expense recorded"); load();
  };

  return (
    <div>
      <PageHeader title="Accounting" subtitle="Income, expenses & P&amp;L"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button className="btn-primary" data-testid="add-expense-btn">+ Record expense</button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record expense</DialogTitle></DialogHeader>
              <form onSubmit={addExpense} className="space-y-3">
                <select data-testid="expense-category-select" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.category} onChange={e=>setForm({...form, category:e.target.value})}>
                  {["Rent","Salaries","Utilities","Marketing","Materials","Other"].map(x=><option key={x}>{x}</option>)}
                </select>
                <input required type="number" placeholder="Amount" data-testid="expense-amount-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})}/>
                <textarea placeholder="Description" data-testid="expense-desc-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/>
                <DialogFooter><button className="btn-primary" data-testid="expense-save-btn">Save</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="linkedin-card p-4" data-testid="acc-income-card">
          <div className="kpi-label">Income</div>
          <div className="text-2xl font-semibold text-[#057642] mt-1">₹{income.toLocaleString('en-IN')}</div>
        </div>
        <div className="linkedin-card p-4" data-testid="acc-expense-card">
          <div className="kpi-label">Expenses</div>
          <div className="text-2xl font-semibold text-[#CC1016] mt-1">₹{expenseTotal.toLocaleString('en-IN')}</div>
        </div>
        <div className="linkedin-card p-4" data-testid="acc-profit-card">
          <div className="kpi-label">Net Profit</div>
          <div className={`text-2xl font-semibold mt-1 ${profit >= 0 ? 'text-[#0A66C2]' : 'text-[#CC1016]'}`}>₹{profit.toLocaleString('en-IN')}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold text-[#00000099] uppercase tracking-wider mb-2">Recent payments (income)</h3>
          <DataTable
            testid="payments-table"
            columns={[
              { key:"receipt_no", label:"Receipt" },
              { key:"student_name", label:"Student" },
              { key:"method", label:"Method" },
              { key:"amount", label:"Amount", render: r => `₹${(r.amount||0).toLocaleString('en-IN')}` },
            ]}
            rows={payments.slice(0, 10)}
          />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[#00000099] uppercase tracking-wider mb-2">Recent expenses</h3>
          <DataTable
            testid="expenses-table"
            columns={[
              { key:"date", label:"Date" },
              { key:"category", label:"Category" },
              { key:"description", label:"Note" },
              { key:"amount", label:"Amount", render: r => `₹${(r.amount||0).toLocaleString('en-IN')}` },
            ]}
            rows={expenses}
          />
        </div>
      </div>
    </div>
  );
}
